import { Button } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { Download, FolderOpen, RotateCcw, Save, SquareArrowOutUpRight } from "lucide-react";
import { useState, type CSSProperties } from "react";

import type { GenerationOutput, Job } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { useJob } from "../../../platform/jobs/detail/job-query";
import { outputMedium } from "../../../platform/jobs/detail/outputs";
import { useDownloadOutput } from "../../../platform/jobs/download-output";
import { expiredLine, isExpired } from "../../../platform/jobs/expired";
import { failedWords } from "../../../platform/jobs/failed-words";
import { useSaveOutput } from "../../../platform/jobs/SaveOutput";
import { isSettled } from "../../../platform/jobs/status";
import { MediaExpired, MediaFailed, MediaLoading } from "../../../ui/MediaCard";
import { ThumbImage } from "../../../ui/ThumbImage";
import classes from "./simple.module.css";

/** The result's frame at `ratio` ("9 / 16"): its width follows it, so a portrait is narrower, not taller. */
function frameStyle(ratio: string | null): CSSProperties {
  return { "--simple-ratio": ratio ?? "1" } as CSSProperties;
}

/** A job's aspect ratio ("9:16") as a CSS ratio, while its output is made. */
const ratioOfJob = (job: Job) => job.params?.aspect_ratio?.replace(":", " / ") ?? null;

interface SimpleResultProps {
  teamId: string;
  brandId: string;
  jobId: string;
  /** Starts the same draft again (a new job); none for a template's run, whose form stays open. */
  onAgain?: (() => void) | undefined;
  /** Clears the result; the kind, product and prompt stay. */
  onAnother: () => void;
}

/**
 * The started job: the current Studio's loader while it is made, then its output with Download,
 * Open in Pro (Pro's Generations with this job's detail) and Save to library, which then opens the
 * saved file in Pro's Library. A job that made nothing says why, with Try again.
 */
export function SimpleResult({ teamId, brandId, jobId, onAgain, onAnother }: SimpleResultProps) {
  const read = useJob(teamId, jobId);
  const job = read.data;
  const readAgain = () => void read.refetch();
  const output = job?.outputs.find((each) => each.status === "delivered" && each.url !== null);
  let body;
  if (job === undefined) {
    body = read.isError ? (
      <ErrorAlert error={read.error} />
    ) : (
      <div className={classes.frame} style={frameStyle(null)}>
        <MediaLoading label="Being made" />
      </div>
    );
  } else if (output !== undefined) {
    body = <Delivered teamId={teamId} brandId={brandId} job={job} output={output} readAgain={readAgain} />;
  } else if (job.outputs.some(isExpired)) {
    body = (
      <div className={classes.frame} style={frameStyle(ratioOfJob(job))}>
        <MediaExpired title={expiredLine(job)} medium={outputMedium(job)} />
      </div>
    );
  } else if (!isSettled(job.status)) {
    body = (
      <div className={classes.frame} style={frameStyle(ratioOfJob(job))}>
        <MediaLoading aspectRatio={job.params?.aspect_ratio ?? null} label="Being made" medium={outputMedium(job)} />
      </div>
    );
  } else {
    body = (
      <>
        <MediaFailed {...failedWords(job.type, job.error?.message)} medium={outputMedium(job)} />
        {onAgain === undefined ? null : (
          <div className={classes.actions}>
            <Button leftSection={<RotateCcw size={16} />} onClick={onAgain}>
              Try again
            </Button>
          </div>
        )}
      </>
    );
  }
  return (
    <section className={classes.step} aria-labelledby="simple-result">
      <div className={classes.resultHead}>
        <h2 id="simple-result" className={classes.stepTitle}>
          Your result
        </h2>
        <span className={classes.grow} />
        <Button variant="subtle" onClick={onAnother}>
          Make another
        </Button>
      </div>
      <div className={classes.result}>{body}</div>
    </section>
  );
}

interface DeliveredProps {
  teamId: string;
  brandId: string;
  job: Job;
  output: GenerationOutput;
  readAgain: () => void;
}

function Delivered({ teamId, brandId, job, output, readAgain }: DeliveredProps) {
  const [savedId, setSavedId] = useState<string | null>(output.saved_asset_id ?? null);
  const download = useDownloadOutput(job, output, readAgain);
  const save = useSaveOutput({
    teamId,
    brandId,
    jobId: job.id,
    index: output.index,
    onSaved: (asset) => setSavedId(asset.id),
  });
  const video = outputMedium(job) === "video";
  const ratio = output.width === null || output.height === null ? ratioOfJob(job) : `${output.width} / ${output.height}`;
  return (
    <>
      <div className={`${classes.frame} ${classes.media}`} style={frameStyle(ratio)}>
        {video ? (
          // A video's poster first, as its card's; the file plays only when asked. A generated
          // video has no captions to offer.
          // oxlint-disable-next-line jsx-a11y/media-has-caption
          <video controls preload="none" src={output.url ?? undefined} poster={output.poster_url ?? undefined} aria-label="The video" />
        ) : (
          <ThumbImage thumb={output.thumb_url} full={output.url ?? ""} alt="The image" onLost={readAgain} />
        )}
      </div>
      <div className={classes.actions}>
        <Button leftSection={<Download size={16} />} loading={download.busy} onClick={download.start}>
          Download
        </Button>
        <Button
          variant="default"
          leftSection={<SquareArrowOutUpRight size={16} />}
          renderRoot={(props) => (
            <Link
              {...props}
              to="/teams/$teamId/brands/$brandId/home2/generations"
              params={{ teamId, brandId }}
              search={{ job: job.id }}
            />
          )}
        >
          Open in Pro
        </Button>
        {savedId === null ? (
          <Button variant="default" leftSection={<Save size={16} />} loading={save.saving} onClick={save.save}>
            Save to library
          </Button>
        ) : (
          <Button
            variant="default"
            leftSection={<FolderOpen size={16} />}
            renderRoot={(props) => (
              <Link
                {...props}
                to="/teams/$teamId/brands/$brandId/home2/library/files"
                params={{ teamId, brandId }}
                search={{ preview: savedId }}
              />
            )}
          >
            Open in Library
          </Button>
        )}
      </div>
      {download.result}
      {savedId === null ? save.result : null}
    </>
  );
}
